import axios from "axios";
import { markAccountChanged } from "./account_changed_noise";

const AUTH_RELOAD_KEY = "tkc_auth_redirect_at";
const AUTH_RELOAD_COOLDOWN_MS = 10000; // 10 seconds

/**
 * Check if this looks like a redirect to the sign-in page.
 * This can happen when the server returns a 200 with HTML instead of JSON.
 */
const isRedirectToSignIn = (response) => {
  // Check if the response URL indicates a redirect to sign-in
  const responseUrl = response?.request?.responseURL;
  if (
    typeof responseUrl === "string" &&
    responseUrl.includes("/users/sign_in")
  ) {
    return true;
  }

  // Check content-type - if we expected JSON but got HTML, that's suspicious
  const contentType = response?.headers?.["content-type"] || "";
  const isHtmlResponse = contentType.includes("text/html");
  const data = response?.data;

  // If we got HTML and it contains the sign-in form, it's an auth redirect
  if (
    isHtmlResponse &&
    typeof data === "string" &&
    data.includes("sign-in-page__form-block")
  ) {
    return true;
  }

  return false;
};

/**
 * Create a custom error class for auth redirects.
 * This allows downstream catch blocks to identify and handle these specially.
 */
class AuthRedirectError extends Error {
  constructor(message = "Session expired") {
    super(message);
    this.name = "AuthRedirectError";
    this.isAuthRedirect = true;
  }
}

/**
 * Navigate to sign-in page when auth has expired.
 * Uses sessionStorage to prevent reload loops.
 */
const navigateToSignIn = () => {
  if (typeof window === "undefined") return false;
  if (typeof sessionStorage === "undefined") return false;

  // Already on sign-in page, don't redirect
  if (window.location.pathname === "/users/sign_in") return false;

  // Check if we recently triggered an auth redirect to prevent loops
  const lastRedirect = sessionStorage.getItem(AUTH_RELOAD_KEY);
  if (lastRedirect) {
    const elapsed = Date.now() - parseInt(lastRedirect, 10);
    if (elapsed < AUTH_RELOAD_COOLDOWN_MS) {
      // Already redirected recently, don't loop
      return false;
    }
  }

  // Record the redirect timestamp
  sessionStorage.setItem(AUTH_RELOAD_KEY, Date.now().toString());

  // Navigate to sign-in page (more deterministic than reload)
  window.location.assign("/users/sign_in");
  return true;
};

// The account this page was rendered for, sent on our own requests so the
// server can refuse one from a page left behind by an account switch (the
// session moved to another account but the page never reloaded). Must match
// ExpectedUserGuard::EXPECTED_USER_HEADER.
const EXPECTED_USER_HEADER = "X-TKC-Expected-User-Id";
const ACCOUNT_CHANGED_RELOAD_KEY = "tkc_account_changed_reload_at";
const ACCOUNT_CHANGED_RELOAD_COOLDOWN_MS = 10000; // 10 seconds

// ONLY our own origin. Axios also PUTs video bytes straight to S3 presigned
// URLs; a custom header there would fail S3's CORS preflight and break every
// upload.
const isSameOriginRequest = (config) => {
  if (typeof window === "undefined") return false;
  const url = config?.url;
  if (typeof url !== "string" || url === "") return false;
  try {
    const resolved = new URL(url, config.baseURL || window.location.href);
    return resolved.origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * Reload as the account the session really has, once. Uses sessionStorage
 * to prevent reload loops, like navigateToSignIn.
 */
const reloadForAccountChange = () => {
  if (typeof window === "undefined") return false;
  if (typeof sessionStorage === "undefined") return false;

  const lastReload = sessionStorage.getItem(ACCOUNT_CHANGED_RELOAD_KEY);
  if (lastReload) {
    const elapsed = Date.now() - parseInt(lastReload, 10);
    if (elapsed < ACCOUNT_CHANGED_RELOAD_COOLDOWN_MS) {
      return false;
    }
  }

  sessionStorage.setItem(ACCOUNT_CHANGED_RELOAD_KEY, Date.now().toString());
  window.location.assign("/");
  return true;
};

// Set default headers to request JSON responses
axios.defaults.headers.common["Accept"] = "application/json";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

axios.interceptors.request.use((config) => {
  const userId =
    typeof window !== "undefined" ? window._tkcCurrentUserId : null;
  if (userId && config?.headers && isSameOriginRequest(config)) {
    if (typeof config.headers.set === "function") {
      config.headers.set(EXPECTED_USER_HEADER, String(userId));
    } else {
      config.headers[EXPECTED_USER_HEADER] = String(userId);
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    // Check if we got a sign-in page instead of the expected response
    if (isRedirectToSignIn(response)) {
      navigateToSignIn();
      return Promise.reject(new AuthRedirectError());
    }

    return response;
  },
  (error) => {
    const response = error?.response;

    // Handle 401 responses or sign-in redirects
    if (response?.status === 401 || isRedirectToSignIn(response)) {
      navigateToSignIn();
      // Tag the error so downstream handlers can identify it
      error.isAuthRedirect = true;
    }

    // 402 Payment Required is an EXPECTED business state on this app, not a
    // bug: see VerifyPlanFeature#respond_to_plan_feature_denial! and
    // Api::RestApiController#verify_company_billing_status on the Rails side,
    // both of which return it deliberately for a suspended/unentitled
    // company. Every billing-page call site that can receive one already
    // catches it and shows its own message (payment_method.js, saved_card_
    // list.js, resubscribe_panel.js, outstanding_invoice_card.js,
    // switch_plan_modal.js, accelerator_card.js, invoice_history_modal.js),
    // several of which also call Honeybadger.notify(err) on it as existing,
    // intentional telemetry. That is the actual source of HB #130636645: not
    // an uncaught crash (its notices carry axios's own message verbatim, with
    // none of the "UnhandledPromiseRejectionWarning:" prefix Honeybadger's own
    // automatic unhandled-rejection listener adds), but an expected,
    // already-handled business state still being reported as noise. Tagged
    // here, matching the isAuthRedirect pattern above, so any call site can
    // branch on it; actually keeping it out of Honeybadger is done in
    // packs/app.js's beforeNotify, which is the one place that sees every
    // notify() call regardless of which catch block sent it.
    if (response?.status === 402) {
      error.isPaymentRequired = true;
    }

    // The session now belongs to a different account than this page (see
    // ExpectedUserGuard): the server ran nothing. Reload as the real account.
    if (
      response?.status === 409 &&
      response?.data?.error === "account_changed"
    ) {
      markAccountChanged();
      reloadForAccountChange();
      error.isAccountChanged = true;
    }

    // Enrich error with request context for Honeybadger
    if (error?.config) {
      error.metadata = {
        request_url: error.config.url,
        request_method: error.config.method,
        response_status: error.response?.status,
      };
    }

    return Promise.reject(error);
  },
);

export { AuthRedirectError };
